import { useEffect, useRef, useState } from 'react';
import {
  AnchorLocations,
  ArrowOverlay,
  BlankEndpoint,
  BrowserUIReact,
  DEFAULT,
  JsPlumbToolkitSurfaceComponent,
  LabelOverlay,
  Surface,
  SurfaceMode,
  SurfaceRenderOptions,
} from '@jsplumbtoolkit/browser-ui-react';
import { OrthogonalConnector } from '@jsplumbtoolkit/connector-orthogonal';
import { AbsoluteLayout } from '@jsplumbtoolkit/core';
import { Spin } from 'antd';
import { round } from 'lodash-es';

import { classnames } from 'lib/classnames';
import { NodeProps, TransitionProps } from 'lib/types/workflow';
import { NodeEnum } from 'lib/workflow';

import { SLIDER_NUM } from './controls/slider';
import Port from './nodes/Port';
import StartNode from './nodes/StartNode';
import TaskNode from './nodes/TaskNode';
import { DEFAULT_ZOOM, initialFlowData, plumbMapping } from './config';
import { WorkflowEditorSlider } from './controls';

import '@jsplumbtoolkit/browser-ui/css/jsplumbtoolkit.css';
import '@jsplumbtoolkit/connector-editors/css/jsplumbtoolkit-connector-editors.css';
import styles from './Palette.module.less';

const cx = classnames(styles);
export interface RefProps {
  deleteItem: (id: string, type: string, callback?: () => void) => void;
  updateItemProperties: (id: string, type: string, data: Record<string, any>) => void;
  getData: () => { transitions: TransitionProps[]; nodes: NodeProps[] };
}

interface PaletteProps {
  initialData: { nodes: NodeProps[]; transitions: TransitionProps[] };
  status?: string;
  className?: string;
  changeZoom?: boolean;
}

const View: React.FC<PaletteProps> = ({ initialData, status, className, changeZoom }) => {
  const [surface, setSurface] = useState<Surface>(null);
  const workflowWrapper = useRef(null);
  const toolkitInstance = useRef<BrowserUIReact>(new BrowserUIReact({}));
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    toolkitInstance.current?.clear();
  }, [initialData]);

  const view = {
    nodes: {
      [NodeEnum.Start]: {
        jsx: (ctx: any) => {
          return <StartNode ctx={ctx} {...ctx} viewer />;
        },
      },
      [NodeEnum.Task]: {
        jsx: (ctx: any) => {
          return <TaskNode ctx={ctx} {...ctx} viewer />;
        },
      },
    },
    edges: {
      [DEFAULT]: {
        endpoint: {
          type: BlankEndpoint.type,
          options: {
            radius: 4,
          },
        },
        // 线条连接样式-流程图样式
        connector: {
          type: OrthogonalConnector.type,
          options: { cornerRadius: 4 },
        },
        // 线条样式
        paintStyle: {
          strokeWidth: 1,
          stroke: '#DADDE3',
          outlineWidth: 2,
          outlineStroke: 'blank',
        },
        hoverPaintStyle: { strokeWidth: 1, stroke: '#3683FF' },
        // 线条箭头
        overlays: [
          {
            type: ArrowOverlay.type,
            options: {
              location: 1,
              width: 10,
              length: 10,
              foldback: 1,
            },
          },
          {
            type: LabelOverlay.type,
            options: {
              label: '${label}',
            },
          },
        ],
      },
    },
    ports: {
      LeftTop: {
        component: Port,
        maxConnections: -1,
        anchor: [0.25, 0, 0, -1],
      },
      RightTop: {
        component: Port,
        maxConnections: -1,
        anchor: [0.75, 0, 0, -1],
      },
      LeftBottom: {
        component: Port,
        maxConnections: -1,
        anchor: [0.25, 1, 0, 1],
      },
      RightBottom: {
        component: Port,
        maxConnections: -1,
        anchor: [0.75, 1, 0, 1],
      },
      Top: {
        component: Port,
        maxConnections: -1,
        anchor: [AnchorLocations.Top],
      },
      Right: {
        component: Port,
        maxConnections: -1,
        anchor: [AnchorLocations.Right],
      },
      Left: {
        component: Port,
        maxConnections: -1,
        anchor: [AnchorLocations.Left],
      },
      Bottom: {
        component: Port,
        maxConnections: -1,
        anchor: [AnchorLocations.Bottom],
      },
    },
  };

  const renderParams: SurfaceRenderOptions = {
    id: 'viewer',
    wheel: {
      zoom: false,
    },
    layout: {
      type: AbsoluteLayout.type,
    },
    events: {
      zoom: (data: any) => {
        setZoom(data.zoom);
      },
    },
    consumeRightClick: false,
    zoomToFit: false,
    zoomRange: [SLIDER_NUM.MIN, SLIDER_NUM.MAX],
    elementsDraggable: false,
    mode: SurfaceMode.DISABLED,
  };

  useEffect(() => {
    if (toolkitInstance.current && surface && initialData?.nodes.length) {
      // 这个settimeout用于加载完成的处理
      setTimeout(() => {
        const data = plumbMapping(initialFlowData(initialData.nodes, initialData.transitions));
        toolkitInstance.current.load({ data });
        setTimeout(() => {
          surface.zoomToFit({ doNotZoomIfVisible: true });
          const node = toolkitInstance.current.getNode(status);
          toolkitInstance.current.toggleSelection(node);
          setLoading(false);
        }, 100);
      }, 100);
    }
  }, [initialData, status, surface]);

  useEffect(() => {
    if (surface) {
      setZoom(1);
      surface.setZoom(1);
    }
    surface?.zoomToFit({ doNotZoomIfVisible: true });
  }, [changeZoom, surface]);

  useEffect(() => {
    const wheelChange = (e: WheelEvent) => {
      setZoom(zoom => {
        const zoomValue = Math.min(Math.max(round(zoom - e.deltaY / 300, 2), SLIDER_NUM.MIN), SLIDER_NUM.MAX);
        surface.setZoom(zoomValue);
        return zoomValue;
      });
    };
    if (surface) {
      const jtk = document.querySelector('.jtk-draggable');
      if (jtk) {
        jtk.addEventListener('wheel', wheelChange);
      }
    }
    return () => {
      const jtk = document.querySelector('.jtk-draggable');
      return jtk?.removeEventListener('wheel', wheelChange);
    };
  }, [surface]);

  return (
    <div className={cx('workflow-palette-container')}>
      <div className={cx('workflow-wrapper', 'viewer', className)} ref={workflowWrapper}>
        <Spin spinning={loading}>
          {toolkitInstance.current && (
            <JsPlumbToolkitSurfaceComponent
              toolkit={toolkitInstance.current}
              view={view}
              renderParams={renderParams}
              ref={(data: JsPlumbToolkitSurfaceComponent) => {
                if (data != null) {
                  setSurface(data.surface);
                }
              }}
            />
          )}
        </Spin>
      </div>
      <div className={cx('slider-wrapper')}>
        {surface && <WorkflowEditorSlider zoom={zoom} setZoom={setZoom} surface={surface} />}
      </div>
    </div>
  );
};

export default View;
